// Suppliers search + list page.
//
// Mirrors customers/index.tsx but filters by capability instead of tier.
// Capability filter is a row of pills covering the most-common CC sourcing
// buckets.
//
// W8-A: Active/Archived tab toggle, admin-gated "+ New supplier" button
// that opens an inline modal with the new-supplier form (no route hop).
// The modal posts directly to the cc-os-mcp Edge Function via a small
// JSON-RPC helper because mcp.addSupplier is not yet wired in
// portal/src/lib/mcp.ts (the orchestrator integrates parallel-agent verbs
// at the end of the wave to avoid races). Once integrated, the helper
// call below should be replaced with mcp.addSupplier(...) — search for
// `INTEGRATE: mcp.addSupplier` to find the swap point.

import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Loader2, AlertCircle, Plus, Search, X } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { SupplierCard } from "@/components/SupplierCard";
import { PermissionGate, ReadOnlyBanner } from "@/components/PermissionGate";
import { cn } from "@/lib/cn";
import { useMcp, mcp } from "@/lib/mcp";
import { CRM_SUPPLIERS_STATUS } from "@/lib/db-enums.generated";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { COUNTRY_OPTIONS, normalizeCountry } from "@/lib/countries";
import { useRealtimeRefresh } from "@/hooks/useRealtimeRefresh";
import { useFormDraft } from "@/hooks/useFormDraft";
import { useListStateRestore } from "@/hooks/useListStateRestore";
import { MCP_FUNCTIONS_URL } from "@/lib/config";
import { supabase } from "@/lib/supabase";

const DEBOUNCE_MS = 300;

// W9 dropdowns audit: capability filter is now derived from the live
// supplier set (distinct unioned `capabilities` arrays) instead of a
// hand-curated list. Computed inside the page from the same searchSuppliers
// response that drives the result cards — no extra round-trip.
function deriveCapabilityOptions(
  rows: Array<{ capabilities?: string[] | null }>,
): Array<{ value: string; label: string }> {
  const set = new Set<string>();
  for (const r of rows) {
    for (const c of r.capabilities ?? []) {
      const trimmed = String(c).trim().toLowerCase();
      if (trimmed) set.add(trimmed);
    }
  }
  const list = Array.from(set).sort();
  return [
    { value: "", label: "All" },
    ...list.map((v) => ({
      value: v,
      label: v.charAt(0).toUpperCase() + v.slice(1),
    })),
  ];
}

type ArchivedTab = "active" | "archived";

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

function SkeletonCard() {
  return (
    <Card>
      <CardContent className="space-y-2 p-4">
        <div className="h-4 w-3/4 animate-pulse rounded bg-muted" />
        <div className="h-3 w-1/2 animate-pulse rounded bg-muted" />
        <div className="h-3 w-1/3 animate-pulse rounded bg-muted" />
      </CardContent>
    </Card>
  );
}

// ─── Direct cc-os-mcp call helper ────────────────────────────────────────
// Mirrors callTool() in lib/mcp.ts. Used while mcp.addSupplier is not yet
// in the wrapper (orchestrator wires it after parallel agents finish).
//
// INTEGRATE: replace usages with mcp.addSupplier(args) once mcp.ts is
// updated by the orchestrator.
async function callMcpDirect<T>(name: string, args: unknown): Promise<T> {
  const url = import.meta.env.VITE_CC_OS_MCP_URL || `${MCP_FUNCTIONS_URL}/cc-os-mcp`;
  let accessToken: string | null = null;
  try {
    const { data } = await supabase.auth.getSession();
    accessToken = data.session?.access_token ?? null;
  } catch {
    /* fall through with no auth header */
  }
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (accessToken) headers["Authorization"] = `Bearer ${accessToken}`;

  const res = await fetch(url, {
    method: "POST",
    headers,
    body: JSON.stringify({
      jsonrpc: "2.0",
      id: crypto.randomUUID(),
      method: "tools/call",
      params: { name, arguments: args },
    }),
  });
  if (!res.ok) throw new Error(`MCP HTTP ${res.status}: ${res.statusText}`);
  const json = (await res.json()) as {
    result?: { content?: Array<{ type: "text"; text: string }>; isError?: boolean };
    error?: { message: string };
  };
  if (json.error) throw new Error(`MCP RPC error: ${json.error.message}`);
  if (!json.result) throw new Error("MCP RPC missing result");
  const text = json.result.content?.[0]?.text ?? "{}";
  const parsed = JSON.parse(text) as Record<string, unknown>;
  if (json.result.isError) {
    const msg = typeof parsed.error === "string" ? parsed.error : "Unknown MCP error";
    // Surface the underlying detail (e.g. PostgREST/RPC error message)
    // when the EF wrapped a downstream failure with
    // `asError(name + ' failed', detail)`. Without this the user only
    // sees the generic "add_supplier failed" with no clue why.
    const detail = typeof parsed.details === "string" && parsed.details.length > 0
      ? `: ${parsed.details}`
      : "";
    throw new Error(`MCP tool error: ${msg}${detail}`);
  }
  return parsed as T;
}

// ─── Inline new-supplier modal ───────────────────────────────────────────

const newSupplierSchema = z.object({
  name: z.string().min(2, "Name is required (min 2 chars)"),
  slug: z.string().regex(
    /^[a-z0-9]+(-[a-z0-9]+)*$/,
    "Slug must be kebab-case (lowercase, digits, hyphens)",
  ),
  short_name: z.string().optional(),
  country: z.string().min(2, "Country is required (type a country name or ISO-2 code)"),
  region: z.string().optional(),
  status: z.enum(["active", "paused", "archived"]),
  capabilities_csv: z.string().optional(),
  capacity_pcs_month: z.string().optional(),
  lead_time_days: z.string().optional(),
  notes: z.string().optional(),
});
type NewSupplierForm = z.infer<typeof newSupplierSchema>;

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

async function sha256Hex(input: string): Promise<string> {
  const buf = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest("SHA-256", buf);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

function NewSupplierModal({
  open, onClose, onCreated,
}: { open: boolean; onClose: () => void; onCreated: () => void }) {
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const {
    register, handleSubmit, setValue, reset, watch, formState: { errors, dirtyFields },
  } = useForm<NewSupplierForm>({
    resolver: zodResolver(newSupplierSchema),
    defaultValues: { status: "active", country: "China" },
  });
  const slugTouched = dirtyFields.slug;

  useEffect(() => {
    if (open) {
      reset({ status: "active", country: "China" });
      setSubmitError(null);
    }
  }, [open, reset]);

  // Draft persistence — keep typed entries alive across accidental refreshes
  // / modal closes. Hook runs only while the modal is open; the restore
  // happens after the open-reset above so a saved draft wins over the
  // defaults. Cleared after a successful submit.
  // eslint-disable-next-line react-hooks/incompatible-library -- react-hook-form's watch() re-renders on every field change via its own subscription outside compiler tracking; draftValues is intentionally re-read fresh each render for local draft persistence, never memoized.
  const draftValues = watch();
  const draft = useFormDraft<typeof draftValues>({
    formId: "supplier:new",
    version: 1,
    values: draftValues,
    setValues: (v) => reset(v),
    enabled: open,
  });

  // Surface a quiet "Draft restored" toast the first time it actually
  // happens so the user knows the form was repopulated.
  useEffect(() => {
    if (draft.restored) {
      toast.message("Draft restored", {
        description: "Picked up where you left off — your saved fields and images are back.",
      });
    }
     
  }, [draft.restored]);

  if (!open) return null;

  const onSubmit = async (values: NewSupplierForm) => {
    setSubmitError(null);
    setSubmitting(true);
    try {
      const idempotency_key = await sha256Hex(`${values.slug}\n${values.name}`);
      const capabilities = values.capabilities_csv
        ? values.capabilities_csv
            .split(",")
            .map((s) => s.trim())
            .filter((s) => s.length > 0)
        : undefined;
      // INTEGRATE: mcp.addSupplier(args) — currently calling the EF
      // directly because the orchestrator hasn't wired the wrapper yet.
      const result = await callMcpDirect<{ supplier: { id: string; name: string }; was_new: boolean }>(
        "add_supplier",
        {
          slug: values.slug,
          name: values.name,
          short_name: values.short_name || undefined,
          country: normalizeCountry(values.country) ?? values.country.toUpperCase(),
          region: values.region || undefined,
          status: values.status,
          capabilities,
          capacity_pcs_month: values.capacity_pcs_month
            ? Number(values.capacity_pcs_month)
            : undefined,
          lead_time_days: values.lead_time_days
            ? Number(values.lead_time_days)
            : undefined,
          notes: values.notes || undefined,
          idempotency_key,
        },
      );
      if (result.was_new) {
        toast.success(`Supplier "${values.name}" created`);
      } else {
        toast.message(
          `Supplier "${values.name}" already existed — opened existing record`,
        );
      }
      // Supplier committed — drop the local draft so the next open of the
      // modal starts blank.
      await draft.clearDraft();
      onCreated();
      onClose();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setSubmitError(msg);
      toast.error("Could not create supplier");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="new-supplier-modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <Card className="w-full max-w-2xl">
        <CardHeader className="flex-row items-start justify-between space-y-0">
          <CardTitle id="new-supplier-modal-title">Add a supplier</CardTitle>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close">
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                {...register("name", {
                  onChange: (e) => {
                    if (!slugTouched) setValue("slug", slugify(e.target.value));
                  },
                })}
                placeholder="e.g. Klevas Garments"
                autoFocus
              />
              {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="slug">Slug</Label>
              <Input id="slug" {...register("slug")} className="font-mono" placeholder="auto-generated from name" />
              <p className="text-xs text-muted-foreground">Lowercase, hyphens only.</p>
              {errors.slug && <p className="text-xs text-destructive">{errors.slug.message}</p>}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="country">Country</Label>
                <Input
                  id="country"
                  {...register("country")}
                  list="supplier-country-options"
                  placeholder="Start typing — e.g. China, Bangladesh, India"
                  maxLength={56}
                  autoComplete="off"
                />
                {/*
                  Native HTML5 datalist gives us autocomplete-by-name with
                  zero deps. Each option's `value` is what lands in the
                  input after selection (the country NAME, so the admin
                  sees what they typed); `normalizeCountry()` in onSubmit
                  converts the name to ISO-2 before sending it to the DB.
                  Free-text values still pass through (uppercased) so an
                  unusual country isn't blocked.
                */}
                <datalist id="supplier-country-options">
                  {COUNTRY_OPTIONS.map((c) => (
                    <option key={c.code} value={c.name}>
                      {c.flag} {c.code}
                    </option>
                  ))}
                </datalist>
                {errors.country && <p className="text-xs text-destructive">{errors.country.message}</p>}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="region">Region / city</Label>
                <Input id="region" {...register("region")} placeholder="Guangdong" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="short_name">Internal codename</Label>
                <Input id="short_name" {...register("short_name")} placeholder="GLOBAL" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="status">Status</Label>
                <Select id="status" {...register("status")}>
                  {CRM_SUPPLIERS_STATUS.map((s) => (
                    <option key={s} value={s}>
                      {s.charAt(0).toUpperCase() + s.slice(1)}
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="capacity_pcs_month">Capacity (pcs/month)</Label>
                <Input id="capacity_pcs_month" type="number" min={0} {...register("capacity_pcs_month")} placeholder="50000" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="lead_time_days">Lead time (days)</Label>
                <Input id="lead_time_days" type="number" min={0} {...register("lead_time_days")} placeholder="60" />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="capabilities_csv">Capabilities (comma-separated)</Label>
              <Input id="capabilities_csv" {...register("capabilities_csv")} placeholder="knitwear, denim, fleece" />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="notes">Notes</Label>
              <Textarea id="notes" {...register("notes")} rows={3} />
            </div>

            {submitError && (
              <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <div>
                  <p className="font-medium">Submission failed</p>
                  <p className="text-xs">{submitError}</p>
                </div>
              </div>
            )}

            <div className="flex items-center gap-3 pt-2">
              <Button type="submit" disabled={submitting}>
                {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                Create supplier
              </Button>
              <Button type="button" variant="ghost" onClick={onClose}>
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── List page ───────────────────────────────────────────────────────────

export function SuppliersListPage() {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const listKey = location.pathname + location.search;
  useListStateRestore({ key: listKey });
  const initialQuery = params.get("q") ?? "";
  const initialCapability = params.get("capability") ?? "";
  const initialTab: ArchivedTab = params.get("tab") === "archived" ? "archived" : "active";
  const [queryRaw, setQueryRaw] = useState(initialQuery);
  const [tab, setTab] = useState<ArchivedTab>(initialTab);
  const [modalOpen, setModalOpen] = useState(false);
  const debouncedQuery = useDebouncedValue(queryRaw, DEBOUNCE_MS);
  const capability = initialCapability;
  const { isAdmin } = useIsAdmin();

  useEffect(() => {
    const next = new URLSearchParams(params);
    if (debouncedQuery) next.set("q", debouncedQuery);
    else next.delete("q");
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery]);

  // Mirror archived tab into URL.
  useEffect(() => {
    const next = new URLSearchParams(params);
    if (tab === "archived") next.set("tab", "archived");
    else next.delete("tab");
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const args = useMemo(() => ({
    query: debouncedQuery,
    ...(capability ? { capability } : {}),
    limit: 50,
  }), [debouncedQuery, capability]);

  const { data, error, loading, refresh } = useMcp(
    () => mcp.searchSuppliers(args),
    [debouncedQuery, capability, tab],
  );

  useRealtimeRefresh(
    [
      { schema: "crm", table: "suppliers" },
      { schema: "samples", table: "supplier_tags" },
    ],
    refresh,
  );

  // Capability chips — derived from a one-shot unfiltered fetch so the
  // chip list doesn't shrink when the user narrows the filter. Cached at
  // the component level via useMcp's effect-based fetch.
  const { data: capabilityProbe } = useMcp(
    () => mcp.searchSuppliers({ query: "", limit: 500 }),
    [],
  );
  const capabilityOptions = useMemo(
    () => deriveCapabilityOptions(capabilityProbe?.suppliers ?? []),
    [capabilityProbe],
  );

  // The search verb returns suppliers regardless of archived_at. Filter on
  // the client until the verb is bumped to accept an `archived` param.
  // (Most archived suppliers will be a small slice, so client-side is fine.)
  const suppliers = useMemo(() => {
    const rows = data?.suppliers ?? [];
    return rows.filter((s) => {
      const archivedAt = (s as Record<string, unknown>).archived_at as string | null | undefined;
      const isArchived = Boolean(archivedAt);
      return tab === "archived" ? isArchived : !isArchived;
    });
  }, [data, tab]);

  const updateCapability = (value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set("capability", value);
    else next.delete("capability");
    setParams(next, { replace: true });
  };

  return (
    <div className="space-y-6">
      <ReadOnlyBanner />
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Suppliers</h2>
          <p className="text-sm text-muted-foreground">
            Search factories and trading partners by name, slug, or capability.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin ? (
            <PermissionGate>
              <Button onClick={() => setModalOpen(true)}>
                <Plus className="h-4 w-4" />
                New supplier
              </Button>
            </PermissionGate>
          ) : (
            // Non-admins still see a way through if a forms route gets added
            // later. For now hide entirely so they don't hit a dead link.
            <Button asChild variant="outline" className="hidden">
              <Link to="#">
                <Plus className="h-4 w-4" />
                New supplier
              </Link>
            </Button>
          )}
        </div>
      </div>

      {/* Active / Archived tab toggle */}
      <div className="flex items-center gap-1 border-b">
        {(["active", "archived"] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={cn(
              "border-b-2 px-3 py-2 text-sm capitalize transition-colors",
              tab === t
                ? "border-primary font-medium text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
            aria-current={tab === t ? "page" : undefined}
          >
            {t}
          </button>
        ))}
      </div>

      <Card>
        <CardHeader className="flex-col gap-3 space-y-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              autoFocus
              value={queryRaw}
              onChange={(e) => setQueryRaw(e.target.value)}
              placeholder="Search by name or slug..."
              className="pl-9"
              aria-label="Search suppliers"
            />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs uppercase tracking-wide text-muted-foreground">Capability</span>
            {capabilityOptions.map((c) => (
              <button
                key={c.value || "all"}
                type="button"
                onClick={() => updateCapability(c.value)}
                className={cn(
                  "rounded-full border px-3 py-1 text-xs transition-colors",
                  capability === c.value
                    ? "border-primary bg-primary/10 font-medium text-primary"
                    : "border-input bg-background text-muted-foreground hover:bg-muted",
                )}
              >
                {c.label}
              </button>
            ))}
          </div>
        </CardHeader>

        <CardContent>
          {loading && (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
            </div>
          )}

          {!loading && error && (
            <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <div>
                <p className="font-medium">Search failed</p>
                <p className="text-xs">{error.message}</p>
              </div>
            </div>
          )}

          {!loading && !error && suppliers.length === 0 && (
            <div className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
              <p>
                {tab === "archived"
                  ? "No archived suppliers."
                  : "Nothing here yet — try a different search or pick another capability."}
              </p>
            </div>
          )}

          {!loading && !error && suppliers.length > 0 && (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {suppliers.map((s) => (
                <SupplierCard key={s.id} supplier={s} listKey={listKey} />
              ))}
            </div>
          )}

          {loading && debouncedQuery && (
            <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" />
              Searching
            </div>
          )}
        </CardContent>
      </Card>

      <CardTitle className="sr-only">Supplier search results</CardTitle>

      <NewSupplierModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onCreated={refresh}
      />
    </div>
  );
}
